import React, { useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import DashboardSidebar from "../../agencyDashboardSidebar";
import PipelineTabs, { Tab } from "../../_shared/PipelineTabs";
import { getJobMetadata } from "../../../../../services/staffingService";

import OfficerListing from "./recommendations/listing";
import OfferStepper from "./status/stepper/OfferStepper";
import { getOffer } from "../../../../../services/staffingService";

const Assign = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1"); // Default to tab "1"
  
  // Original offer.jsx state
  const [eventKey, setEventKey] = useState("1");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [recommendationsCount, setRecommendationsCount] = useState(0);
  const [officersCount, setOfficersCount] = useState(0)
  const [offersMetadata, setOffersMetadata] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  
  // Moved from status.jsx - for individual offer details
  const [offer, setOffer] = useState({});
  const [negotiations, setNegotiations] = useState({});
  const [job, setJob] = useState({});
  const [allOffers, setAllOffers] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [staff, setStaff] = useState({});
  
  const { providerCompanyId, jobId } = useParams();
  
  let view= 'recommended';
  // let view= 'closest';
  const [listViewType, setListViewType] = useState(view === 'recommended' ? 'recommended' : 'closest');

  React.useEffect(() => {
    // fetchJobs()
    // total recommendations count
    fetchJobAndOfferMetadata();
    // offers with name of a resource
  }, []);

  // Moved from status.jsx - fetch individual offer data
  React.useEffect(() => {
    if (selectedOfferId) {
      fetchOfferData();
    }
  }, [selectedOfferId]);

  async function fetchJobAndOfferMetadata() {
    setLoading(true);
    const resp = await getJobMetadata(providerCompanyId, jobId);
    // setRecommendationsCount(resp.data.totalRecommendations);
    const offers = resp.data.offers || [];
    setOffersMetadata([...offers]);
    setSelectedOfferId(offers.length > 0 ? offers[0]._id : null);
    setLoading(false);
  }

  // Moved from status.jsx - fetch individual offer details
  async function fetchOfferData() {
    if (!selectedOfferId) return;
    
    setLoading(true);
    try {
      const { data } = await getOffer(providerCompanyId, selectedOfferId);
      setOffer(data.offer);
      setNegotiations(data.offer.negotiations);
      setLastUpdated(new Date().toLocaleString());
      setJob(data.job);
      setAllOffers([...data.all_offers]);
      setStaff(data.staff);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching offer data:", error);
      setLoading(false);
    }
  }

  const handleOfferSelection = (offerId) => {
    setSelectedOfferId(offerId);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-inter">
      <DashboardSidebar isOpen={showSideDrawer} setIsOpen={setShowSideDrawer} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 h-full flex flex-col">

          {/* Back Button — Dispoline pattern */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/jobs`)}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-fit"
          >
            <ArrowLeft size={16} />
            Back to Jobs
          </button>

          {/* Page Header — Dispoline pattern */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground">
              {job?.customer_company_id?.companyName || 'Shift Offers'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {job?.address?.formattedAddress || 'Manage recommendations and sent offers for this job'}
            </p>
          </div>

          {/* Tabs — fills remaining height */}
          <div className="flex-1 min-h-0">
            <PipelineTabs
              activeKey={activeTab}
              onSelect={handleTabChange}
              className="h-full"
            >
                  <Tab eventKey="1" label="Recommendations" count={officersCount}>
                    <div className="h-full">
                      <OfficerListing
                        providerCompanyId={providerCompanyId}
                        jobId={jobId}
                        setListViewType={setListViewType}
                        listViewType={listViewType}
                        setSelectedOfferId={setSelectedOfferId}
                        handleTabChange={handleTabChange}
                        setActiveTab={setActiveTab}
                        setOfficersCount={setOfficersCount}
                        officersCount={officersCount}
                        fetchJobAndOfferMetadata={fetchJobAndOfferMetadata}
                      />
                    </div>
                  </Tab>

                  <Tab eventKey="2" label="Sent Offers" count={offersMetadata.length}>
                    <div className="h-full flex flex-col">
                      {offersMetadata.length === 0 && (
                        <div className="text-center text-gray-500 p-5">
                          No Offers sent
                        </div>
                      )}
                      {offersMetadata.length > 0 && (
                        <>
                          {/* Compact Offer Navigation */}
                          <div className="mb-4 flex-shrink-0">
                            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                            {offersMetadata.map((offer, index) => {
                              const user = offer.provider_resource_id.user_id;
                              const profileName = `${user?.firstName} ${user?.lastName}`;
                              const isSelected = offer._id === selectedOfferId;
                              
                              // Define consistent color themes for each status
                              const getStatusTheme = (status, selected) => {
                                const themes = {
                                  pending: {
                                    selected: "bg-yellow-50 text-yellow-800 border-yellow-400",
                                    unselected: "bg-white border-yellow-200 text-yellow-700 hover:border-yellow-300"
                                  },
                                  accepted: {
                                    selected: "bg-green-50 text-green-800 border-green-400",
                                    unselected: "bg-white border-green-200 text-green-700 hover:border-green-300"
                                  },
                                  rejected: {
                                    selected: "bg-red-50 text-red-800 border-red-400",
                                    unselected: "bg-white border-red-200 text-red-700 hover:border-red-300"
                                  },
                                  disputed: {
                                    selected: "bg-orange-50 text-orange-800 border-orange-400",
                                    unselected: "bg-white border-orange-200 text-orange-700 hover:border-orange-300"
                                  },
                                  expired: {
                                    selected: "bg-gray-50 text-gray-800 border-gray-400",
                                    unselected: "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                                  }
                                };
                                
                                const theme = themes[status.toLowerCase()] || {
                                  selected: "bg-blue-50 text-blue-800 border-blue-400",
                                  unselected: "bg-white border-blue-200 text-blue-700 hover:border-blue-300"
                                };
                                
                                return selected ? theme.selected : theme.unselected;
                              };

                              return (
                                <button
                                  key={offer._id}
                                  onClick={() => handleOfferSelection(offer._id)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 whitespace-nowrap ${
                                    getStatusTheme(offer.status, isSelected)
                                  } ${
                                    isSelected 
                                      ? "shadow-md border-2" 
                                      : "hover:shadow-sm border"
                                  }`}
                                >
                                  {/* Avatar */}
                                  <div className={`w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ${
                                    isSelected ? "ring-1 ring-white ring-opacity-60" : ""
                                  }`}>
                                    {user?.profilePictureUrl ? (
                                      <img
                                        src={user?.profilePictureUrl}
                                        alt={profileName}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                                        {profileName.split(' ').map(n => n[0]).join('')}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Name and Status */}
                                  <div className="flex flex-col items-start">
                                    <span className="text-sm font-medium leading-none">
                                      {profileName}
                                    </span>
                                    <span className="text-xs opacity-75 leading-none mt-0.5">
                                      {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                                      {offer.status.toLowerCase() === "disputed" && " - Action Needed"}
                                    </span>
                                  </div>
                                  
                                  {/* Status Indicator Dot */}
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    offer.status.toLowerCase() === "pending" ? "bg-yellow-500" :
                                    offer.status.toLowerCase() === "accepted" ? "bg-green-500" :
                                    offer.status.toLowerCase() === "rejected" ? "bg-red-500" :
                                    offer.status.toLowerCase() === "disputed" ? "bg-orange-500" :
                                    offer.status.toLowerCase() === "expired" ? "bg-gray-500" :
                                    "bg-blue-500"
                                  }`} />
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {selectedOfferId && Object.keys(offer).length > 0 && Object.keys(staff).length > 0 && (
                          <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 flex flex-col">
                            <div className="flex-1 min-h-0 overflow-y-auto p-3">
                              <OfferStepper
                                offer={offer}
                                negotiations={negotiations}
                                setNegotiations={setNegotiations}
                                setOffer={setOffer}
                                offerId={selectedOfferId}
                                providerCompanyId={providerCompanyId}
                                jobId={jobId}
                              />
                            </div>
                          </div>
                        )}
                        
                        {selectedOfferId && Object.keys(offer).length === 0 && !loading && (
                          <div className="flex items-center justify-center h-32">
                            <div className="text-gray-500">Loading offer details...</div>
                          </div>
                        )}
                      </>
                    )}
                    </div>
                  </Tab>
            </PipelineTabs>
          </div>{/* end flex-1 min-h-0 tabs wrapper */}
        </div>{/* end p-6 */}
      </main>

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default Assign;
